import { utcToZonedTime } from 'date-fns-tz'
import { checkSnooze } from './checkSnooze'
import { convertHours } from './prepTimeFunctions'
import { Item } from '../state/types'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

type ConvertedHours = {
	[key: number]: {
		startTimes: number[]
		endTimes: number[]
	}
}

export const formatTime = (decimal: number): string => {
	if (decimal === 24) return `11:59pm`
	let hours = Math.floor(decimal)
	let minutes = Math.round((decimal - hours) * 60)
	if (minutes === 60) {
		minutes = 0
		hours += 1
	}
	const period = hours >= 12 ? 'pm' : 'am'
	const formattedHours = hours % 12 || 12
	return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`
}

export const formatTimeWithoutPadding = (decimal: number): string => {
	if (decimal === 24) return `11:59 pm`
	let hours = Math.floor(decimal)
	let minutes = Math.round((decimal - hours) * 60)
	if (minutes === 60) {
		minutes = 0
		hours += 1
	}
	const displayMinutes = minutes > 0 ? minutes : ''
	const normalizedHours = hours % 24
	const period = normalizedHours >= 12 ? 'pm' : 'am'
	const formattedHours = hours % 12 || 12
	if (minutes > 0) {
		return `${formattedHours}:${displayMinutes.toString()} ${period}`
	}
	return `${formattedHours} ${period}`
}

const is24_7 = (hours: ConvertedHours | null | undefined): boolean => {
	if (!hours || Object.keys(hours).length === 0) return true

	return Object.values(hours).every(dayHours => {
		if (!dayHours || Object.keys(dayHours).length === 0) return true

		return (
			dayHours.startTimes &&
			dayHours.endTimes &&
			dayHours.startTimes.length === 1 &&
			dayHours.endTimes.length === 1 &&
			dayHours.startTimes[0] === 0 &&
			dayHours.endTimes[0] === 24
		)
	})
}

export const getOrgRestrainedHours = (
	restaurantHours: any,
	orgHoursData: any,
	allowOrdersOutsideOrgHours: boolean
): ConvertedHours | null => {
	// If restaurantHours is null, it means always available
	if (restaurantHours === null) {
		return null
	}

	const orgHours = convertHours(orgHoursData, 0)
	if (is24_7(restaurantHours) && (!orgHours || allowOrdersOutsideOrgHours)) {
		return null
	}

	const orgRestrainedHours: ConvertedHours = {}
	for (let i = 0; i < 7; i++) {
		const restDayHours =
			restaurantHours[i] && restaurantHours[i].startTimes
				? restaurantHours[i]
				: { startTimes: [0], endTimes: [24] }
		const orgDayHours =
			orgHours && !allowOrdersOutsideOrgHours
				? orgHours[i] || { startTimes: [0], endTimes: [24] }
				: { startTimes: [0], endTimes: [24] }

		orgRestrainedHours[i] = { startTimes: [], endTimes: [] }

		for (let j = 0; j < restDayHours.startTimes.length; j++) {
			if (restDayHours.startTimes[j] === 0 && restDayHours.endTimes[j] === 0) continue // Closed all day
			if (restDayHours.startTimes[j] === 0 && restDayHours.endTimes[j] === 24) {
				orgRestrainedHours[i] = orgDayHours
				break
			}

			for (let k = 0; k < orgDayHours.startTimes.length; k++) {
				if (orgDayHours.startTimes[k] === 0 && orgDayHours.endTimes[k] === 0) continue
				if (orgDayHours.startTimes[k] === 0 && orgDayHours.endTimes[k] === 24) {
					orgRestrainedHours[i].startTimes.push(restDayHours.startTimes[j])
					orgRestrainedHours[i].endTimes.push(restDayHours.endTimes[j])
					continue
				}

				const start = Math.max(restDayHours.startTimes[j], orgDayHours.startTimes[k])
				const end = Math.min(restDayHours.endTimes[j], orgDayHours.endTimes[k])
				if (start < end) {
					orgRestrainedHours[i].startTimes.push(start)
					orgRestrainedHours[i].endTimes.push(end)
				}
			}
		}
	}
	return orgRestrainedHours
}

export const itemAvailabilityCheck = (
	items: any[],
	orgHours: any,
	allowOrdersOutsideOrgHours: boolean,
	localTimezone: string,
	currTime: any = null,
	restaurantHours: any = null,
	timeHoursDifference: number
): boolean => {
	const currentTime = currTime !== null ? currTime : new Date()

	const currentDecimalTime = currentTime.getHours() + currentTime.getMinutes() / 60
	const day = currentTime.getDay() // Get the current day (0 = Sunday, 1 = Monday, etc.)

	for (const item of items) {
		let { hours } = item
		if (!hours && restaurantHours) {
			hours = restaurantHours
		} else if (!hours) {
			return true
		}

		const orgRestrainedHours = getOrgRestrainedHours(hours, orgHours, allowOrdersOutsideOrgHours)
		if (orgRestrainedHours === null) {
			continue
		}

		// Adjust the item available time to match restaurant's timezone b/c restaurant hours are fixed in local timezone
		const itemAvailableTime = currentDecimalTime - timeHoursDifference

		const todayHours = orgRestrainedHours[day] || { startTimes: [], endTimes: [] }
		const isAvailable = todayHours.startTimes.some(
			(start, index) => itemAvailableTime >= start && itemAvailableTime < todayHours.endTimes[index]
		)
		if (!isAvailable) {
			return false
		}
	}
	// If all items are available at the scheduled time, return true
	return true
}

export const isWithinOrgHours = (
	orgHours: any,
	localTimezone: string,
	currTime: any = null
): boolean => {
	const orgHoursConverted = convertHours(orgHours, 0)
	if (!orgHoursConverted) return true // If no org hours set, always return true

	const currentTime = currTime !== null ? currTime : utcToZonedTime(new Date(), localTimezone)
	const day = currentTime.getDay()
	const currentDecimalTime = currentTime.getHours() + currentTime.getMinutes() / 60

	const todayOrgHours = orgHoursConverted[day] || { startTimes: [], endTimes: [] }

	return todayOrgHours.startTimes.some(
		(start, index) =>
			currentDecimalTime >= start && currentDecimalTime < todayOrgHours.endTimes[index]
	)
}

export const isRestaurantOpen = (
	restaurant: any,
	orgHours: any,
	allowOrdersOutsideOrgHours: boolean,
	localTimezone: string,
	currTime: any = null
): boolean => {
	const { hours, prepTime = 0, isSnoozed, snoozeUntil } = restaurant

	if (checkSnooze(isSnoozed, snoozeUntil)) {
		return false
	}

	const orgRestrainedHours = getOrgRestrainedHours(hours, orgHours, allowOrdersOutsideOrgHours)

	if (orgRestrainedHours === null) return true // 24/7 open

	const currentTime = currTime !== null ? currTime : utcToZonedTime(new Date(), localTimezone)

	const day = currentTime.getDay()
	const currentDecimalTime = currentTime.getHours() + currentTime.getMinutes() / 60

	const todayHours = orgRestrainedHours[day] || { startTimes: [], endTimes: [] }

	const isOpen = todayHours.startTimes.some((start, index) => {
		// For opening time, no prep time consideration
		const afterOpeningTime = currentDecimalTime >= start

		// For closing time, consider prep time only if ignorePrepTimeAtClose is false
		const closingTimeWithPrep = global.ignorePrepTimeAtClose
			? todayHours.endTimes[index]
			: todayHours.endTimes[index] - prepTime / 60

		const beforeClosingTime = currentDecimalTime < closingTimeWithPrep

		return afterOpeningTime && beforeClosingTime
	})

	return isOpen
}

export const getHoursStatus = (
	restaurant: any,
	orgHours: any,
	allowOrdersOutsideOrgHours: boolean,
	localTimezone: string,
	timeHoursDifference: number
) => {
	const { hours, isSnoozed, snoozeUntil } = restaurant

	const calculatedAt = Date.now()

	if (checkSnooze(isSnoozed, snoozeUntil)) {
		return { message: 'Not accepting new orders right now.', calculatedAt }
	}
	const orgRestrainedHours = getOrgRestrainedHours(hours, orgHours, allowOrdersOutsideOrgHours)

	if (orgRestrainedHours === null) return { message: 'Open 24/7', calculatedAt }

	const currentTime = utcToZonedTime(new Date(), localTimezone)

	const isOpen = isRestaurantOpen(
		restaurant,
		orgHours,
		allowOrdersOutsideOrgHours,
		localTimezone,
		currentTime
	)

	if (isOpen) {
		const day = currentTime.getDay()
		const currentDecimalTime = currentTime.getHours() + currentTime.getMinutes() / 60
		const todayHours = orgRestrainedHours[day] || { startTimes: [], endTimes: [] }
		const closingTimeIndex = todayHours.endTimes.findIndex(end => currentDecimalTime < end)
		if (closingTimeIndex !== -1) {
			const closingTime = todayHours.endTimes[closingTimeIndex] + timeHoursDifference

			return {
				message: `Open now until ${formatTimeWithoutPadding(closingTime)}`,
				calculatedAt,
				closingTime,
			}
		}
	}

	// Find next opening time
	for (let i = 0; i < 7; i++) {
		const nextDay = (currentTime.getDay() + i) % 7
		const nextDayHours = orgRestrainedHours[nextDay] || { startTimes: [], endTimes: [] }

		if (nextDayHours.startTimes.length > 0) {
			const nextOpeningTime = nextDayHours.startTimes[0] + timeHoursDifference

			// If it's today, ensure the opening time is in the future
			if (i === 0) {
				const currentHour = currentTime.getHours() + currentTime.getMinutes() / 60
				if (nextOpeningTime <= currentHour) {
					// Skip today as its opening time has passed
					continue
				}
			}

			const dayDescription = i === 0 ? 'today' : i === 1 ? 'tomorrow' : DAYS[nextDay]
			return {
				message: `Currently closed. Opens ${dayDescription} at ${formatTimeWithoutPadding(nextOpeningTime)}`,
				calculatedAt,
				nextOpeningTime,
			}
		}
	}

	return { message: 'Currently closed.', calculatedAt }
}

const formatHours = (time: number): string => {
	const hour = Math.floor(time)
	const period = hour >= 12 ? 'pm' : 'am'
	const formattedHour = hour > 12 ? hour - 12 : hour
	return `${formattedHour} ${period}`
}

export const getCurrentOrgDayHours = (orgHoursRaw: any): string => {
	const orgHours = convertHours(orgHoursRaw, 0)

	if (!orgHours) return 'OPEN 24/7'

	const today = new Date().getDay()
	const todayHours = orgHours[today]

	if (!todayHours?.startTimes?.length || !todayHours?.endTimes?.length) {
		return 'CLOSED'
	}

	// Just get the first start and end time of the day
	const openTime = formatHours(todayHours.startTimes[0])
	const closeTime = formatHours(todayHours.endTimes[0])

	return `${openTime} - ${closeTime}`
}

export const areAllRestaurantsInCartOpen = (
	cart: any[],
	restaurants: any[],
	orgHours: any,
	allowOrdersOutsideOrgHours: boolean,
	localTimezone
): boolean => {
	return cart.every(cartItem => {
		const matchingRestaurant = restaurants.find(restaurant => restaurant.id === cartItem.rId)
		if (!matchingRestaurant) {
			return false
		}
		return isRestaurantOpen(matchingRestaurant, orgHours, allowOrdersOutsideOrgHours, localTimezone)
	})
}

export const epochToHours = (epochTime: number) => {
	const currentTime = new Date(epochTime)
	const hours = currentTime.getHours()
	const minutes = currentTime.getMinutes() / 60
	return hours + minutes
}
